import React, { useEffect, useState } from "react";
import api from "../../api/axios";

function VoucherRangePrint({ referenceStart, referenceEnd, branchId }) {
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const token = localStorage.getItem("accessToken");

    useEffect(() => {
        setLoading(true);
        setError("");

        api.post(`/vouchers/by-reference-range`, {
            reference_start: referenceStart,
            reference_end: referenceEnd,
            branch_id: branchId
        }, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((response) => {
                if (response.data.status === 1) {
                    setVouchers(response.data.vouchers);
                } else {
                    setError("Vouchers not found.");
                }
            })
            .catch((err) => {
                console.error("Error fetching range documents:", err);
                setError(err.response?.data?.message || "Failed to load vouchers in this reference range.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [referenceStart, referenceEnd, branchId]);

    // Isolated Print Window approach to guarantee multiple page breakdown mechanics bypass dashboard CSS boundaries
    const handlePrintAll = () => {
        const printContent = document.getElementById("printable-vouchers-area").innerHTML;
        const originalTitle = document.title;

        // Open an unconstrained empty window instance
        const printWindow = window.open("", "_blank", "width=900,height=700");

        printWindow.document.open();
        printWindow.document.write(`
            <html>
                <head>
                    <title>Batch Journal Print - ${referenceStart} to ${referenceEnd}</title>
                    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css">
                    <style>
                        body {
                            background-color: #ffffff !important;
                            color: #000000 !important;
                            font-size: 13px;
                            padding: 20px;
                        }
                        .single-voucher-block {
                            page-break-after: always !important;
                            break-after: page !important;
                            margin-bottom: 40px !important;
                            padding-bottom: 20px !important;
                            display: block !important;
                        }
                        .single-voucher-block:last-child {
                            page-break-after: avoid !important;
                            break-after: avoid !important;
                            border-bottom: none !important;
                        }
                        .voucher-header-box {
                            background-color: #f8f9fa !important;
                            border: 1px solid #dee2e6 !important;
                            padding: 15px !important;
                            margin-bottom: 20px !important;
                            border-radius: 6px;
                        }
                        .vh-item {
                            margin-bottom: 6px;
                        }
                        .vh-label {
                            font-weight: bold;
                            color: #212529;
                        }
                        th {
                            background-color: #e6e6e6 !important;
                            color: #000000 !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        @media print {
                            body { padding: 0; }
                            .voucher-header-box {
                                background-color: #f4f5f7 !important; 
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="container-fluid">
                        ${printContent}
                    </div>
                    <script>
                        // Wait until resources and stylesheets compile, then fire layout handler
                        window.addEventListener('DOMContentLoaded', () => {
                            setTimeout(() => {
                                window.print();
                                window.close();
                            }, 500);
                        });
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    if (loading) return <div className="p-3"><p>Loading vouchers in range...</p></div>;
    if (error) return <div className="p-3"><div className="alert alert-danger">{error}</div></div>;

    return (
        <div className="voucher-batch-print-container p-2">
            {/* Screen View Only Style Rule Adjustments */}
            <style>{`
                .single-voucher-block {
                    border-bottom: 2px dashed #ccc;
                    padding-bottom: 30px;
                    margin-bottom: 30px;
                }
                .single-voucher-block:last-child {
                    border-bottom: none;
                }
                .voucher-header-box {
                    background-color: #f8f9fa;
                }
                .vh-label {
                    font-weight: bold;
                    display: inline-block;
                    margin-right: 5px;
                }
            `}</style>

            {/* Print Control Action Bar */}
            <div className="d-flex justify-content-between align-items-center mb-4 p-2 bg-light rounded border">
                <span className="text-muted small">
                    Loaded <strong>{vouchers.length}</strong> journal vouchers in sequence.
                </span>
                <button className="btn btn-danger btn-sm px-4 fw-bold" onClick={handlePrintAll}>
                    <i className="ri-printer-line me-1"></i> Print All Vouchers
                </button>
            </div>

            {/* Target Canvas DOM Element Area Captured by Javascript window instance */}
            <div id="printable-vouchers-area">
                {vouchers.map((voucher) => {
                    const currentEntries = voucher.data || [];
                    const totalDebit = currentEntries.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0);
                    const totalCredit = currentEntries.reduce((sum, item) => sum + parseFloat(item.credit || 0), 0);

                    return (
                        <div key={voucher.id} className="single-voucher-block">
                            {/* Company / Hub Details Header */}
                            <div className="voucher-top-header text-center mb-3">
                                <h4 className="company-name mb-0 fw-bold">
                                    {voucher.branch?.company?.name || ""} ({voucher.branch?.name || ""})
                                </h4>
                                <h6 className="document-title mt-1 text-uppercase fw-semibold tracking-wider text-muted">
                                    General Journal
                                </h6>
                            </div>

                            {/* Metadata Card: Uses grid layouts to completely solve tight spacing or overlap issues */}
                            <div className="voucher-header-box mb-3 border">
                                <div className="row">
                                    <div className="col-md-4 col-4 vh-item">
                                        <div className="vh-label">Reference No:</div>
                                        <div className="vh-value text-break">{voucher.series_no}</div>
                                    </div>
                                    <div className="col-md-4 col-4 vh-item">
                                        <div className="vh-label">Voucher No:</div>
                                        <div className="vh-value text-break">{voucher.voucher_no}</div>
                                    </div>
                                    <div className="col-md-4 col-4 vh-item">
                                        <div className="vh-label">Date:</div>
                                        <div className="vh-value">
                                            {new Date(voucher.voucher_date).toLocaleDateString("en-GB")}
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-2 border-top pt-2">
                                    <div className="col-12 vh-item">
                                        <div className="vh-label">Narration:</div>
                                        <div className="vh-value text-wrap text-muted">
                                            {voucher.narration || "N/A"}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Entries Table Split */}
                            <table className="table table-bordered table-sm voucher-table">
                                <thead>
                                    <tr>
                                        <th width="30%">Account</th>
                                        <th width="50%">Description</th>
                                        <th width="10%" className="text-end">Debit</th>
                                        <th width="10%" className="text-end">Credit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentEntries.map((row, idx) => (
                                        <tr key={row.id || idx}>
                                            <td>{row.ledger?.ledger_code} - {row.ledger?.ledger_name}</td>
                                            <td>{row.description || ""}</td>
                                            <td className="text-end">
                                                {row.debit ? parseFloat(row.debit).toFixed(2) : ""}
                                            </td>
                                            <td className="text-end">
                                                {row.credit ? parseFloat(row.credit).toFixed(2) : ""}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="table-light fw-bold">
                                        <td colSpan="2" className="text-end text-uppercase small">Total</td>
                                        <td className="text-end">{totalDebit.toFixed(2)}</td>
                                        <td className="text-end">{totalCredit.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default VoucherRangePrint;